import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  cities: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const citySlice = createSlice({
  name: 'city',
  initialState,
  reducers: {
    setCities: (state, action) => {
      state.cities = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setCities, setTotalCount, setTotalPages, setLoading, setError } = citySlice.actions;

// Get cities (paginated, filtered by stateId/countryId & searchable)
export const getCities = (filters = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const stateId = filters.stateId || '';
    const countryId = filters.countryId || '';

    const params = { page, limit };
    if (search) params.search = search;
    if (stateId) params.stateId = stateId;
    if (countryId) params.countryId = countryId;

    const response = await axiosInstance.get('/admin/city/list', { params });
    if (response.data && response.data.status === true) {
      const result = response.data.data?.result || response.data.data || [];
      const totalCount = response.data.data?.totalCount || (Array.isArray(result) ? result.length : 0);
      const totalPages = response.data.data?.totalPages || 1;
      const citiesList = Array.isArray(result) ? result : [];

      dispatch(setCities(citiesList));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: citiesList, totalCount, totalPages };
    } else {
      dispatch(setCities([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch cities';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setCities([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch cities';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single city details
export const getCityDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/city/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch city details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch city details';
    return { status: false, message: errorMsg };
  }
};

// Add new city
export const addCity = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/city/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add city' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add city';
    return { status: false, message: errorMsg };
  }
};

// Update city details
export const updateCity = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/city/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update city' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update city';
    return { status: false, message: errorMsg };
  }
};

// Delete city
export const deleteCity = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/city/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete city' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete city';
    return { status: false, message: errorMsg };
  }
};

export default citySlice.reducer;
